import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import SEO from '@/components/SEO';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { TrendingUp, Building2, Search, FileText, ArrowRight, BarChart3 } from 'lucide-react';
import { Link } from 'react-router-dom';

export const FuerInvestoren = () => {
  const investmentTypes = [
    {
      icon: Building2,
      title: 'Core-Objekte',
      description: 'Lebensmittelmärkte und Discounter mit langfristigen Mietverträgen von bonitätsstarken Mietern.',
      yield: '4,5-5,5%',
      wault: '> 8 Jahre'
    },
    {
      icon: TrendingUp,
      title: 'Core+',
      description: 'Fachmärkte und Fachmarktzentren mit mittelfristigen Laufzeiten und Entwicklungspotenzial.',
      yield: '5,5-6,5%',
      wault: '5-8 Jahre'
    },
    {
      icon: Search,
      title: 'Value-Add',
      description: 'Objekte mit Optimierungsbedarf bei Mietverträgen oder Vermietungsstand.',
      yield: '6,5-8%+',
      wault: '< 5 Jahre'
    }
  ];

  const services = [
    'Off-Market Deals – Zugang zu Objekten vor öffentlicher Vermarktung',
    'Due Diligence Support – Professionelle Objektprüfung und Risikoanalyse',
    'Marktanalysen – Standortbewertung und Wettbewerbsanalyse',
    'Käuferberatung – Strategische Begleitung bei Portfolioaufbau',
    'Transaction Management – Komplette Abwicklung bis zum Notartermin'
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <SEO
        title="Für Investoren – Handelsimmobilien Investment"
        description="Zugang zu Core- und Core+ Handelsimmobilien. Lebensmittelmärkte, Fachmärkte und Fachmarktzentren mit stabilen Cashflows und langfristigen Mietverträgen."
        url="/fuer-investoren"
      />
      <Navbar />
      <main className="flex-grow">
        {/* Hero */}
        <section className="bg-gradient-to-br from-blue-50 to-white py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl">
              <div className="w-20 h-1 bg-gradient-to-r from-[#D4A853] to-[#F0C870] mb-6"></div>
              <h1 className="text-4xl md:text-5xl font-bold mb-6" style={{ color: 'var(--navy-900)' }}>
                Handelsimmobilien-Investments mit stabilen Cashflows
              </h1>
              <p className="text-lg leading-relaxed mb-8" style={{ color: 'var(--gray-700)' }}>
                Sie suchen Handelsimmobilien als langfristige Kapitalanlage? Wir bieten Ihnen Zugang zu 
                sorgfältig ausgewählten Core- und Core+ Objekten mit bonitätsstarken Mietern.
              </p>
              <Link to="/kontakt">
                <Button className="btn-gold-gradient text-white border-0 font-semibold px-8 py-6">
                  Investmentmöglichkeiten anfragen
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* Investment Types */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <div className="w-16 h-1 bg-gradient-to-r from-[#D4A853] to-[#F0C870] mb-4 mx-auto"></div>
              <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: 'var(--navy-900)' }}>
                Investment-Profile
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {investmentTypes.map((type, index) => {
                const IconComponent = type.icon;
                return (
                  <Card key={index} className="glass-card p-8 border-t-4" style={{ borderTopColor: 'var(--gold-600)' }}>
                    <div className="w-16 h-16 rounded-xl flex items-center justify-center mb-6 shadow-lg" style={{ background: 'linear-gradient(135deg, #0F2744 0%, #2563A0 100%)' }}>
                      <IconComponent className="h-8 w-8 text-white" />
                    </div>
                    <h3 className="text-xl font-bold mb-3" style={{ color: 'var(--navy-800)' }}>
                      {type.title}
                    </h3>
                    <p className="mb-6" style={{ color: 'var(--gray-700)' }}>
                      {type.description}
                    </p>
                    <div className="space-y-2 pt-4 border-t border-gray-200">
                      <div className="flex justify-between">
                        <span className="text-sm font-semibold" style={{ color: 'var(--gray-600)' }}>Rendite:</span>
                        <span className="text-sm font-bold" style={{ color: 'var(--gold-600)' }}>{type.yield}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm font-semibold" style={{ color: 'var(--gray-600)' }}>WAULT:</span>
                        <span className="text-sm font-bold" style={{ color: 'var(--navy-600)' }}>{type.wault}</span>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>

        {/* Services */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <div className="w-16 h-1 bg-gradient-to-r from-[#D4A853] to-[#F0C870] mb-4 mx-auto"></div>
              <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: 'var(--navy-900)' }}>
                Unsere Leistungen für Investoren
              </h2>
            </div>

            <Card className="glass-card p-8">
              <ul className="space-y-4">
                {services.map((service, index) => (
                  <li key={index} className="flex items-start gap-4">
                    <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-1" style={{ backgroundColor: 'var(--gold-600)' }}>
                      <BarChart3 className="h-4 w-4 text-white" />
                    </div>
                    <span className="text-lg" style={{ color: 'var(--gray-800)' }}>{service}</span>
                  </li>
                ))}
              </ul>
            </Card>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-gradient-to-br from-blue-900 to-blue-800 text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Interessiert an Handelsimmobilien?
            </h2>
            <p className="text-lg mb-8 opacity-90">
              Kontaktieren Sie uns für aktuelle Investmentmöglichkeiten und Off-Market Deals.
            </p>
            <Link to="/kontakt">
              <Button className="btn-gold-gradient text-white border-0 font-semibold px-10 py-7 text-lg shadow-2xl">
                Jetzt Kontakt aufnehmen
                <ArrowRight className="ml-2 h-6 w-6" />
              </Button>
            </Link>
          </div>
        </section>

        {/* CTA: Suchprofil anlegen */}
        <section className="py-20 bg-gradient-to-br from-[#0F2744] to-[#2563A0]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">
              Auf der Suche nach einem Objekt?
            </h2>
            <p className="text-lg mb-8 text-gray-100 leading-relaxed">
              Legen Sie kostenlos Ihr Suchprofil an und erhalten Sie sofort Benachrichtigungen, 
              sobald passende Handelsimmobilien verf\u00fcgbar sind.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/kaeufer/suchprofil">
                <Button className="btn-gold-gradient text-white border-0 font-semibold px-10 py-7 text-lg shadow-2xl">
                  <Search className="mr-2 h-6 w-6" />
                  Suchprofil anlegen
                </Button>
              </Link>
              <Link to="/kontakt">
                <Button variant="outline" className="px-10 py-7 text-lg font-semibold bg-white/10 backdrop-blur-sm border-2 border-white/30 text-white hover:bg-white/20">
                  Pers\u00f6nliche Beratung
                  <ArrowRight className="ml-2 h-6 w-6" />
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default FuerInvestoren;